/**
 * TeamDashboard - Dashboard view showing team-specific metrics
 *
 * Displays:
 * - Quick insight cards with key metrics filtered by team
 * - Data table with detailed breakdown for selected team
 * - Team selector in page header
 * - Stacked bar charts showing commit distribution by classification and size
 */

import {
  InsightDashboard,
  InsightDashboardStore,
  MetricsCard,
  PageHeader,
  type SelectOption,
  StackedBarChart,
  type TabDefinition,
  TabsWithStore,
} from "@getdevpulse/shared-ui"
import { observer } from "mobx-react-lite"
import { useMemo, useState } from "react"
import { logger } from "../lib/logger"
import { useDataStore, useI18nStore, useMetadataStore } from "../stores/StoreContext"
import type { ComplexityClass } from "../types/data"

export const TeamDashboard = observer(() => {
  const dataStore = useDataStore()
  const metadataStore = useMetadataStore()
  const i18nStore = useI18nStore()

  // Get all unique teams
  const teams = metadataStore.uniqueTeams

  // Select first team by default
  const [selectedTeam, setSelectedTeam] = useState<string>(teams[0] || "")

  // Create select options for teams
  const teamOptions: SelectOption[] = useMemo(
    () =>
      teams.map((team) => ({
        id: team,
        label: team,
      })),
    [teams],
  )

  // Get author handles for selected team (from teams.json which uses handles as keys)
  const teamAuthorHandles = useMemo(() => {
    if (!selectedTeam) return new Set<string>()

    // teams.json maps author handle -> team name
    // We need to find all handles that belong to the selected team
    const teamMembers = metadataStore.allTeams
      .filter((t) => t.team === selectedTeam)
      .map((t) => t.authorId) // authorId here is actually the handle from teams.json key

    const handles = new Set(teamMembers)

    logger.debug("Team member lookup", {
      component: "TeamDashboard",
      team: selectedTeam,
      teamMembers,
      handles: Array.from(handles),
    })

    return handles
  }, [selectedTeam, metadataStore])

  // Create the dashboard store with computed data filtered by team
  const dashboardStore = useMemo(() => {
    // Filter commits and PRs by team members
    // Note: commits use authorHandle (from DataStore.getAuthorHandle), not authorId
    const commits = dataStore.allCommits.filter((c) => teamAuthorHandles.has(c.authorHandle))
    const prs = dataStore.allPullRequests.filter((pr) => teamAuthorHandles.has(pr.creatorHandle))

    logger.debug("Filtered data for team", {
      component: "TeamDashboard",
      team: selectedTeam,
      totalCommits: dataStore.allCommits.length,
      filteredCommits: commits.length,
      totalPRs: dataStore.allPullRequests.length,
      filteredPRs: prs.length,
      teamAuthorHandles: Array.from(teamAuthorHandles),
      sampleCommitHandles: dataStore.allCommits.slice(0, 5).map((c) => c.authorHandle),
      samplePRHandles: dataStore.allPullRequests.slice(0, 5).map((pr) => pr.creatorHandle),
    })

    // Calculate date range
    const now = new Date()
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000)

    // Helper function to generate daily chart data
    const generateDailyChartData = (
      items: Array<{ timestampDate?: Date; createdAtDate?: Date }>,
      startDate: Date,
      endDate: Date,
    ) => {
      const dailyCounts = new Map<string, number>()
      const start = new Date(startDate)
      start.setHours(0, 0, 0, 0)
      const end = new Date(endDate)
      end.setHours(23, 59, 59, 999)

      // Initialize all days with 0
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toISOString().split("T")[0]
        dailyCounts.set(dateKey, 0)
      }

      // Count items per day
      items.forEach((item) => {
        const date = item.timestampDate || item.createdAtDate
        if (date && date >= start && date <= end) {
          const dateKey = date.toISOString().split("T")[0]
          dailyCounts.set(dateKey, (dailyCounts.get(dateKey) || 0) + 1)
        }
      })

      return Array.from(dailyCounts.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, value]) => ({ value }))
    }

    // Current week metrics
    const currentWeekPRs = prs.filter(
      (pr) => pr.createdAtDate >= oneWeekAgo && pr.state === "merged",
    )
    const currentWeekFeatures = commits.filter(
      (c) => c.timestampDate >= oneWeekAgo && c.commitType === "feature",
    )
    const currentWeekBugfixes = commits.filter(
      (c) => c.timestampDate >= oneWeekAgo && c.commitType === "bugfix",
    )
    const currentWeekMaintenance = commits.filter(
      (c) =>
        c.timestampDate >= oneWeekAgo &&
        c.commitType &&
        c.commitType !== "feature" &&
        c.commitType !== "bugfix",
    )

    // Previous week metrics (for comparison)
    const previousWeekPRs = prs.filter(
      (pr) =>
        pr.createdAtDate >= twoWeeksAgo && pr.createdAtDate < oneWeekAgo && pr.state === "merged",
    )
    const previousWeekFeatures = commits.filter(
      (c) =>
        c.timestampDate >= twoWeeksAgo &&
        c.timestampDate < oneWeekAgo &&
        c.commitType === "feature",
    )
    const previousWeekBugfixes = commits.filter(
      (c) =>
        c.timestampDate >= twoWeeksAgo && c.timestampDate < oneWeekAgo && c.commitType === "bugfix",
    )
    const previousWeekMaintenance = commits.filter(
      (c) =>
        c.timestampDate >= twoWeeksAgo &&
        c.timestampDate < oneWeekAgo &&
        c.commitType &&
        c.commitType !== "feature" &&
        c.commitType !== "bugfix",
    )

    logger.debug("Calculated team metrics", {
      component: "TeamDashboard",
      team: selectedTeam,
      teamMembers: teamAuthorHandles.size,
      currentWeekPRs: currentWeekPRs.length,
      currentWeekFeatures: currentWeekFeatures.length,
      currentWeekBugfixes: currentWeekBugfixes.length,
      currentWeekMaintenance: currentWeekMaintenance.length,
    })

    // Create insights with their associated table data
    const insights = [
      {
        id: "pr-throughput",
        insight: {
          id: "pr-throughput",
          subtitle: i18nStore.t("insightMergedPRs"),
          date: i18nStore.t("quickInsightDate"),
          title: i18nStore.t("insightPRThroughput"),
          value: currentWeekPRs.length.toString(),
          secondaryText: i18nStore.tf("quickInsightVsLastWeek", {
            count: previousWeekPRs.length.toString(),
          }),
          chartData: generateDailyChartData(currentWeekPRs, oneWeekAgo, now),
        },
        tableData: currentWeekPRs.map((pr) => ({
          title: pr.title,
          url: `https://github.com/${pr.repoName}/pull/${pr.number}`,
          author: pr.creatorHandle,
          date: pr.createdAtDate.toLocaleDateString(),
          additions: pr.additions?.toString() || "0",
          deletions: pr.deletions?.toString() || "0",
          files: pr.changedFiles?.toString() || "0",
        })),
        tableColumns: [
          {
            accessorKey: "title",
            header: i18nStore.t("tableColumnTitle"),
            cell: ({
              row,
            }: {
              row: {
                original: { url: string }
                getValue: (key: string) => string
              }
            }) => (
              <a
                href={row.original.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {row.getValue("title")}
              </a>
            ),
          },
          { accessorKey: "author", header: i18nStore.t("tableColumnAuthor") },
          {
            accessorKey: "date",
            header: i18nStore.t("tableColumnCreated"),
          },
          {
            accessorKey: "additions",
            header: i18nStore.t("tableColumnAdditions"),
          },
          {
            accessorKey: "deletions",
            header: i18nStore.t("tableColumnDeletions"),
          },
          { accessorKey: "files", header: i18nStore.t("tableColumnFiles") },
        ],
      },
      {
        id: "features",
        insight: {
          id: "features",
          subtitle: i18nStore.t("insightFeatureCommits"),
          date: "Last 7 days",
          title: i18nStore.t("insightFeatures"),
          value: currentWeekFeatures.length.toString(),
          secondaryText: `vs ${previousWeekFeatures.length} last week`,
          chartData: generateDailyChartData(currentWeekFeatures, oneWeekAgo, now),
        },
        tableData: currentWeekFeatures.map((commit) => ({
          message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
          url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
          author: commit.authorHandle,
          date: commit.timestampDate.toLocaleDateString(),
          score: commit.score.toString(),
          files: commit.filesChanged.toString(),
        })),
        tableColumns: [
          {
            accessorKey: "message",
            header: "Message",
            cell: ({
              row,
            }: {
              row: {
                original: { url: string }
                getValue: (key: string) => string
              }
            }) => (
              <a
                href={row.original.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {row.getValue("message")}
              </a>
            ),
          },
          { accessorKey: "author", header: "Author" },
          { accessorKey: "date", header: "Date" },
          { accessorKey: "score", header: "Score" },
          { accessorKey: "files", header: "Files" },
        ],
      },
      {
        id: "bugfixes",
        insight: {
          id: "bugfixes",
          subtitle: i18nStore.t("insightBugfixCommits"),
          date: "Last 7 days",
          title: i18nStore.t("insightBugfixes"),
          value: currentWeekBugfixes.length.toString(),
          secondaryText: `vs ${previousWeekBugfixes.length} last week`,
          chartData: generateDailyChartData(currentWeekBugfixes, oneWeekAgo, now),
        },
        tableData: currentWeekBugfixes.map((commit) => ({
          message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
          url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
          author: commit.authorHandle,
          date: commit.timestampDate.toLocaleDateString(),
          score: commit.score.toString(),
          files: commit.filesChanged.toString(),
        })),
        tableColumns: [
          {
            accessorKey: "message",
            header: "Message",
            cell: ({
              row,
            }: {
              row: {
                original: { url: string }
                getValue: (key: string) => string
              }
            }) => (
              <a
                href={row.original.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {row.getValue("message")}
              </a>
            ),
          },
          { accessorKey: "author", header: "Author" },
          { accessorKey: "date", header: "Date" },
          { accessorKey: "score", header: "Score" },
          { accessorKey: "files", header: "Files" },
        ],
      },
      {
        id: "maintenance",
        insight: {
          id: "maintenance",
          subtitle: i18nStore.t("insightMaintenanceCommits"),
          date: "Last 7 days",
          title: i18nStore.t("insightMaintenance"),
          value: currentWeekMaintenance.length.toString(),
          secondaryText: `vs ${previousWeekMaintenance.length} last week`,
          chartData: generateDailyChartData(currentWeekMaintenance, oneWeekAgo, now),
        },
        tableData: currentWeekMaintenance.map((commit) => ({
          message: commit.message.slice(0, 60) + (commit.message.length > 60 ? "..." : ""),
          url: `https://github.com/${commit.repoName}/commit/${commit.hash}`,
          author: commit.authorHandle,
          type: commit.commitType || "other",
          date: commit.timestampDate.toLocaleDateString(),
          score: commit.score.toString(),
          files: commit.filesChanged.toString(),
        })),
        tableColumns: [
          {
            accessorKey: "message",
            header: "Message",
            cell: ({
              row,
            }: {
              row: {
                original: { url: string }
                getValue: (key: string) => string
              }
            }) => (
              <a
                href={row.original.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {row.getValue("message")}
              </a>
            ),
          },
          { accessorKey: "author", header: "Author" },
          { accessorKey: "type", header: "Type" },
          { accessorKey: "date", header: "Date" },
          { accessorKey: "score", header: "Score" },
          { accessorKey: "files", header: "Files" },
        ],
      },
    ]

    return InsightDashboardStore.create({
      insights,
      selectedInsightId: "pr-throughput",
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18nStore.locale needed for translation reactivity
  }, [
    dataStore.allCommits,
    dataStore.allPullRequests,
    teamAuthorHandles,
    selectedTeam,
    i18nStore.locale,
  ])

  // Compute bar chart data for team commits
  const barChartData = useMemo(() => {
    // Filter commits by team members
    const commits = dataStore.allCommits.filter((c) => teamAuthorHandles.has(c.authorHandle))

    // Define the order of complexity classes and commit types
    const complexityOrder: ComplexityClass[] = ["XS", "S", "M", "L", "XL"]
    const commitTypeOrder = [
      "feature",
      "bugfix",
      "refactoring",
      "ci",
      "tests",
      "docs",
      "chore",
      "perf",
      "revert",
      "merge",
      "archive",
      "styling", // Renamed from "style" to avoid React reserved prop conflict
      "other",
    ]

    // Map commit types to avoid reserved words
    // "style" -> "styling" to prevent React style prop conflicts
    const mapCommitType = (type: string | undefined): string => {
      if (type === "style") return "styling"
      return type || "unclassified"
    }

    // Chart 1: Classification (x-axis) stacked by Size
    const byClassification = new Map<string, Record<ComplexityClass, number>>()

    for (const commitType of commitTypeOrder) {
      byClassification.set(commitType, {
        XS: 0,
        S: 0,
        M: 0,
        L: 0,
        XL: 0,
      })
    }

    byClassification.set("unclassified", {
      XS: 0,
      S: 0,
      M: 0,
      L: 0,
      XL: 0,
    })

    for (const commit of commits) {
      const type = mapCommitType(commit.commitType)
      const size = commit.complexityClass
      const counts = byClassification.get(type)
      if (counts && size in counts) {
        counts[size] = (counts[size] || 0) + 1
      }
    }

    const classificationData = Array.from(byClassification.entries())
      .filter(([, counts]) => {
        return Object.values(counts).some((v) => v > 0)
      })
      .map(([type, counts]) => ({
        classification: type,
        ...counts,
      }))

    // Chart 2: Size (x-axis) stacked by Classification
    const bySize = new Map<ComplexityClass, Record<string, number>>()

    for (const size of complexityOrder) {
      const typeCounts: Record<string, number> = {}
      for (const type of [...commitTypeOrder, "unclassified"]) {
        typeCounts[type] = 0
      }
      bySize.set(size, typeCounts)
    }

    for (const commit of commits) {
      const type = mapCommitType(commit.commitType)
      const size = commit.complexityClass
      const counts = bySize.get(size)
      if (counts) {
        counts[type] = (counts[type] || 0) + 1
      }
    }

    const activeTypes = new Set<string>()
    for (const [, counts] of bySize.entries()) {
      for (const [type, count] of Object.entries(counts)) {
        if (count > 0) {
          activeTypes.add(type)
        }
      }
    }

    const sizeData = complexityOrder.map((size) => {
      const counts = bySize.get(size)
      if (!counts) return { size }
      const entry: Record<string, string | number> = { size }
      for (const type of activeTypes) {
        entry[type] = counts[type] || 0
      }
      return entry
    })

    return {
      classificationData,
      sizeData,
      complexityOrder,
      activeTypes: Array.from(activeTypes),
    }
  }, [dataStore.allCommits, teamAuthorHandles])

  // Calculate PR metrics for the new PR tab
  const prMetrics = useMemo(() => {
    const prs = dataStore.allPullRequests.filter((pr) => teamAuthorHandles.has(pr.creatorHandle))
    const now = new Date()
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)

    // Current month PRs
    const currentMonthPRs = prs.filter(
      (pr) => pr.mergedAtDate && pr.mergedAtDate >= oneMonthAgo && pr.state === "merged",
    )

    // Previous month PRs (for comparison)
    const previousMonthPRs = prs.filter(
      (pr) =>
        pr.mergedAtDate &&
        pr.mergedAtDate >= twoMonthsAgo &&
        pr.mergedAtDate < oneMonthAgo &&
        pr.state === "merged",
    )

    // Calculate percentage change
    const currentCount = currentMonthPRs.length
    const previousCount = previousMonthPRs.length
    const percentageChange =
      previousCount > 0 ? ((currentCount - previousCount) / previousCount) * 100 : 0

    // Generate daily PR count for chart (last 30 days)
    const dailyPRs = new Map<string, number>()
    for (let i = 0; i < 30; i++) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
      const dateKey = date.toISOString().split("T")[0]
      dailyPRs.set(dateKey, 0)
    }

    currentMonthPRs.forEach((pr) => {
      if (pr.mergedAtDate) {
        const dateKey = pr.mergedAtDate.toISOString().split("T")[0]
        dailyPRs.set(dateKey, (dailyPRs.get(dateKey) || 0) + 1)
      }
    })

    const prChartData = Array.from(dailyPRs.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, value]) => ({
        label: new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value,
      }))

    // Calculate code changes (additions + deletions) for current month
    const dailyCodeChanges = new Map<string, { additions: number; deletions: number }>()
    for (let i = 0; i < 30; i++) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
      const dateKey = date.toISOString().split("T")[0]
      dailyCodeChanges.set(dateKey, { additions: 0, deletions: 0 })
    }

    currentMonthPRs.forEach((pr) => {
      if (pr.mergedAtDate) {
        const dateKey = pr.mergedAtDate.toISOString().split("T")[0]
        const current = dailyCodeChanges.get(dateKey) || {
          additions: 0,
          deletions: 0,
        }
        dailyCodeChanges.set(dateKey, {
          additions: current.additions + pr.additions,
          deletions: current.deletions + pr.deletions,
        })
      }
    })

    const codeChangesChartData = Array.from(dailyCodeChanges.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, { additions, deletions }]) => ({
        label: new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value: additions + deletions,
      }))

    return {
      currentCount,
      previousCount,
      percentageChange,
      prChartData,
      codeChangesChartData,
      trend: (percentageChange > 0 ? "up" : percentageChange < 0 ? "down" : "neutral") as
        | "up"
        | "down"
        | "neutral",
    }
  }, [dataStore.allPullRequests, teamAuthorHandles])

  // Calculate Issue metrics for the new Issues tab
  const issueMetrics = useMemo(() => {
    const issues = dataStore.allIssues.filter((issue) => teamAuthorHandles.has(issue.creatorHandle))
    const now = new Date()
    const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)

    // Current month issues created
    const currentMonthCreated = issues.filter((issue) => issue.createdAtDate >= oneMonthAgo)

    // Previous month issues created (for comparison)
    const previousMonthCreated = issues.filter(
      (issue) => issue.createdAtDate >= twoMonthsAgo && issue.createdAtDate < oneMonthAgo,
    )

    // Current month issues closed
    const currentMonthClosed = issues.filter(
      (issue) =>
        issue.closedAtDate && issue.closedAtDate >= oneMonthAgo && issue.state === "closed",
    )

    // Previous month issues closed (for comparison)
    const previousMonthClosed = issues.filter(
      (issue) =>
        issue.closedAtDate &&
        issue.closedAtDate >= twoMonthsAgo &&
        issue.closedAtDate < oneMonthAgo &&
        issue.state === "closed",
    )

    // Calculate percentage changes
    const createdCount = currentMonthCreated.length
    const previousCreatedCount = previousMonthCreated.length
    const createdPercentageChange =
      previousCreatedCount > 0
        ? ((createdCount - previousCreatedCount) / previousCreatedCount) * 100
        : 0

    const closedCount = currentMonthClosed.length
    const previousClosedCount = previousMonthClosed.length
    const closedPercentageChange =
      previousClosedCount > 0
        ? ((closedCount - previousClosedCount) / previousClosedCount) * 100
        : 0

    // Generate daily issue created count for chart (last 30 days)
    const dailyCreated = new Map<string, number>()
    for (let i = 0; i < 30; i++) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
      const dateKey = date.toISOString().split("T")[0]
      dailyCreated.set(dateKey, 0)
    }

    currentMonthCreated.forEach((issue) => {
      const dateKey = issue.createdAtDate.toISOString().split("T")[0]
      dailyCreated.set(dateKey, (dailyCreated.get(dateKey) || 0) + 1)
    })

    const createdChartData = Array.from(dailyCreated.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, value]) => ({
        label: new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value,
      }))

    // Generate daily issue closed count for chart (last 30 days)
    const dailyClosed = new Map<string, number>()
    for (let i = 0; i < 30; i++) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000)
      const dateKey = date.toISOString().split("T")[0]
      dailyClosed.set(dateKey, 0)
    }

    currentMonthClosed.forEach((issue) => {
      if (issue.closedAtDate) {
        const dateKey = issue.closedAtDate.toISOString().split("T")[0]
        dailyClosed.set(dateKey, (dailyClosed.get(dateKey) || 0) + 1)
      }
    })

    const closedChartData = Array.from(dailyClosed.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, value]) => ({
        label: new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value,
      }))

    return {
      createdCount,
      previousCreatedCount,
      createdPercentageChange,
      createdChartData,
      createdTrend: (createdPercentageChange > 0
        ? "up"
        : createdPercentageChange < 0
          ? "down"
          : "neutral") as "up" | "down" | "neutral",
      closedCount,
      previousClosedCount,
      closedPercentageChange,
      closedChartData,
      closedTrend: (closedPercentageChange > 0
        ? "up"
        : closedPercentageChange < 0
          ? "down"
          : "neutral") as "up" | "down" | "neutral",
    }
  }, [dataStore.allIssues, teamAuthorHandles])

  // Calculate releases metrics for team
  const releasesMetrics = useMemo(() => {
    const now = new Date()
    const twelveWeeksAgo = new Date(now.getTime() - 12 * 7 * 24 * 60 * 60 * 1000)
    const sixWeeksAgo = new Date(now.getTime() - 6 * 7 * 24 * 60 * 60 * 1000)

    // Filter releases by team members (excluding drafts and prereleases)
    const recentReleases = dataStore.allReleases.filter(
      (release) =>
        release.createdAtDate >= twelveWeeksAgo &&
        !release.draft &&
        !release.prerelease &&
        teamAuthorHandles.has(release.authorHandle),
    )

    // Count total releases in last 12 weeks
    const totalReleases = recentReleases.length

    // Create weekly buckets for trend (last 12 weeks)
    const weeklyTrend: Array<{ label: string; value: number }> = []
    for (let i = 11; i >= 0; i--) {
      const weekStart = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000)
      const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000)

      const count = recentReleases.filter(
        (release) => release.createdAtDate >= weekStart && release.createdAtDate < weekEnd,
      ).length

      weeklyTrend.push({
        label: weekStart.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        value: count,
      })
    }

    // Calculate percentage change (comparing first 6 weeks vs last 6 weeks)
    const firstHalf = weeklyTrend.slice(0, 6).reduce((sum, week) => sum + week.value, 0)
    const secondHalf = weeklyTrend.slice(6, 12).reduce((sum, week) => sum + week.value, 0)
    const percentageChange = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0

    // Create stacked bar chart data for last 6 weeks by repository
    const last6WeeksReleases = dataStore.allReleases.filter(
      (release) =>
        release.createdAtDate >= sixWeeksAgo &&
        !release.draft &&
        !release.prerelease &&
        teamAuthorHandles.has(release.authorHandle),
    )

    // Get all unique repositories
    const repositories = Array.from(new Set(last6WeeksReleases.map((r) => r.repoName))).sort()

    // Create weekly buckets for stacked chart
    const stackedData: Array<Record<string, string | number>> = []
    for (let i = 5; i >= 0; i--) {
      const weekStart = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000)
      const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000)

      const weekData: Record<string, string | number> = {
        week: weekStart.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      }

      // Count releases per repository for this week
      repositories.forEach((repo) => {
        const count = last6WeeksReleases.filter(
          (release) =>
            release.repoName === repo &&
            release.createdAtDate >= weekStart &&
            release.createdAtDate < weekEnd,
        ).length
        weekData[repo] = count
      })

      stackedData.push(weekData)
    }

    return {
      totalReleases,
      weeklyTrend,
      percentageChange,
      trend: (percentageChange > 0 ? "up" : percentageChange < 0 ? "down" : "neutral") as
        | "up"
        | "down"
        | "neutral",
      stackedData,
      repositories,
    }
  }, [dataStore.allReleases, teamAuthorHandles])

  // Create tabs
  const tabs: TabDefinition[] = [
    {
      id: "insights",
      label: i18nStore.t("tabPerformance"),
      content: (
        <InsightDashboard
          store={dashboardStore}
          tableTitle={i18nStore.t("chartDetails")}
          dataTableTranslations={{
            filtersButton: i18nStore.t("dataTableFilters"),
            filterBy: i18nStore.t("dataTableFilterBy"),
            noResults: i18nStore.t("dataTableNoResults"),
            rowsPerPage: i18nStore.t("dataTableRowsPerPage"),
            page: i18nStore.t("dataTablePage"),
            of: i18nStore.t("dataTableOf"),
            totalRows: i18nStore.t("dataTableTotalRows"),
          }}
          searchPlaceholder={i18nStore.t("dataTableSearch")}
        />
      ),
    },
    {
      id: "commits",
      label: i18nStore.t("tabScore"),
      content: (
        <div className="grid gap-6 md:grid-cols-2">
          <StackedBarChart
            title={i18nStore.t("chartCommitsByClassification")}
            description="Number of commits grouped by classification (commit type) and stacked by complexity size"
            data={barChartData.classificationData}
            categoryKey="classification"
            stackKeys={barChartData.complexityOrder}
          />
          <StackedBarChart
            title={i18nStore.t("chartCommitsBySize")}
            description="Number of commits grouped by complexity size and stacked by classification (commit type)"
            data={barChartData.sizeData}
            categoryKey="size"
            stackKeys={barChartData.activeTypes}
          />
        </div>
      ),
    },
    {
      id: "prs",
      label: i18nStore.t("tabPRs"),
      content: (
        <div className="grid gap-6 md:grid-cols-2">
          <MetricsCard
            title={i18nStore.t("chartPRsMerged")}
            description={`${prMetrics.currentCount} PRs merged (vs ${prMetrics.previousCount} last month)`}
            percentage={Math.abs(prMetrics.percentageChange)}
            trend={prMetrics.trend}
            chartData={prMetrics.prChartData}
            chartType="bar"
          />
          <MetricsCard
            title={i18nStore.t("chartCodeChanges")}
            description="Lines added and removed from merged PRs"
            percentage={0}
            trend="neutral"
            chartData={prMetrics.codeChangesChartData}
            chartType="line"
          />
        </div>
      ),
    },
    {
      id: "issues",
      label: i18nStore.t("tabIssues"),
      content: (
        <div className="grid gap-6 md:grid-cols-2">
          <MetricsCard
            title={i18nStore.t("chartIssuesCreated")}
            description={`${issueMetrics.createdCount} issues created (vs ${issueMetrics.previousCreatedCount} last month)`}
            percentage={Math.abs(issueMetrics.createdPercentageChange)}
            trend={issueMetrics.createdTrend}
            chartData={issueMetrics.createdChartData}
            chartType="bar"
          />
          <MetricsCard
            title={i18nStore.t("chartIssuesClosed")}
            description={`${issueMetrics.closedCount} issues closed (vs ${issueMetrics.previousClosedCount} last month)`}
            percentage={Math.abs(issueMetrics.closedPercentageChange)}
            trend={issueMetrics.closedTrend}
            chartData={issueMetrics.closedChartData}
            chartType="bar"
          />
        </div>
      ),
    },
    {
      id: "releases",
      label: i18nStore.t("tabReleases"),
      content: (
        <div className="grid gap-6">
          {/* Releases Trend Card */}
          <MetricsCard
            title={i18nStore.t("releasesTitle")}
            description={i18nStore.t("releasesDescription")}
            percentage={Math.abs(releasesMetrics.percentageChange)}
            trend={releasesMetrics.trend}
            chartData={releasesMetrics.weeklyTrend}
            chartType="line"
            trendLabel={i18nStore.t("chartTrend")}
          />

          {/* Stacked Bar Chart - Releases by Repository */}
          <StackedBarChart
            title={i18nStore.t("releasesChartTitle")}
            description={i18nStore.t("releasesChartDescription")}
            data={releasesMetrics.stackedData}
            categoryKey="week"
            stackKeys={releasesMetrics.repositories}
          />
        </div>
      ),
    },
  ]

  // Log dashboard render
  logger.debug("Rendering TeamDashboard", {
    component: "TeamDashboard",
    team: selectedTeam,
    teamMembers: teamAuthorHandles.size,
  })

  // If no teams available, show message
  if (teams.length === 0) {
    return (
      <div className="min-h-full bg-background">
        <div className="container mx-auto p-3 sm:p-4 md:p-6">
          <PageHeader
            title={i18nStore.t("dashboardTeamTitle")}
            subtitle={i18nStore.t("dashboardTeamNoData")}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-background">
      <div className="container mx-auto p-3 sm:p-4 md:p-6">
        <PageHeader
          title={i18nStore.t("dashboardTeamTitle")}
          subtitle={i18nStore.tf("dashboardTeamSubtitle", {
            team: selectedTeam,
          })}
          selectOptions={teamOptions}
          selectedOptionId={selectedTeam}
          onSelectChange={setSelectedTeam}
          selectPlaceholder={i18nStore.t("selectTeam")}
        >
          <TabsWithStore tabs={tabs} defaultSelectedTab="insights" />
        </PageHeader>
      </div>
    </div>
  )
})
